import { defineNuxtPlugin } from '#imports'
import { StatusCodes } from 'http-status-codes'
import { ErrorType, isThrownException } from '@injectivelabs/exceptions'
import { IS_PRODUCTION } from '@/app/utils/constants'
import type { ThrownException } from '@injectivelabs/exceptions'

const parseMessage = (error: any): string => {
  const message = error.message || error

  if (message.toLowerCase().includes('response closed')) {
    return 'Something happened. Please refresh the page.'
  }

  return message
}

const reportToBugSnag = (error: ThrownException) => {
  if (!IS_PRODUCTION) {
    console.warn(error.toCompactError().message)
    console.error(error)

    return
  }

  if (
    ([ErrorType.Unspecified, ErrorType.WalletError] as ErrorType[]).includes(
      error.type
    )
  ) {
    console.warn(error.toCompactError().message)
    console.error(error)
  }
}

const reportUnknownErrorToBugsnag = (error: Error) => {
  if (!IS_PRODUCTION) {
    console.error({ error, stack: error.stack })
  }

  const newError = new Error(
    `The ${error.message} is not handled as an Exception - ${error.stack}`
  )

  console.warn(newError.message, newError.stack)
}

const reportToUser = (error: ThrownException) => {
  const notificationStore = useSharedNotificationStore()

  // Timedout requests happening in the background should not be reported to the user
  if (
    error.type === ErrorType.HttpRequest &&
    error.code === StatusCodes.REQUEST_TOO_LONG
  ) {
    return
  }

  notificationStore.error({ title: parseMessage(error) })
}

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.config.errorHandler = (error, context) => {
    console.error(error, context, (error as any).stack)
  }

  window.onunhandledrejection = function (event: PromiseRejectionEvent) {
    const error = event.reason

    if (!IS_PRODUCTION) {
      return
    }

    if (!isThrownException(error)) {
      reportUnknownErrorToBugsnag(error)
    } else {
      reportToBugSnag(error)
    }
  }

  const errorHandler = (error: ThrownException) => {
    if (!isThrownException(error)) {
      reportUnknownErrorToBugsnag(error)
    }

    reportToUser(error)

    console.warn(error.toObject())
  }

  return {
    provide: {
      onError: errorHandler
    }
  }
})
